// src/components/modals/DefectsModal.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CollapsibleCell from '../common/CollapsibleCell';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface Defect {
    id: string;
    defect_id_str: string;
    test_name: string;
    agent_name: string;
    test_type: string;
    severity: 'Critical' | 'High' | 'Medium' | 'Low';
    status: 'New' | 'Acknowledged' | 'In Progress' | 'Resolved' | 'Closed';
    task_prompt: string;
    ai_analysis: string;
    failure_reason: string;
    created_at: string;
    updated_at: string;
}

interface DefectsModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedAgent: string;
    onDefectUpdate?: () => void;
}

//const DefectsModal: React.FC<DefectsModalProps> = ({ isOpen, onClose, selectedAgent }) => {
const DefectsModal: React.FC<DefectsModalProps> = ({ isOpen, onClose, selectedAgent, onDefectUpdate }) => {
    const [defects, setDefects] = useState<Defect[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [severityFilter, setSeverityFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [testTypeFilter, setTestTypeFilter] = useState('All');

    const fetchDefects = async () => {
        if (!selectedAgent) return;
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ agent_name: selectedAgent });
            if (severityFilter !== 'All') params.append('severity', severityFilter);
            if (statusFilter !== 'All') params.append('status', statusFilter);
            if (testTypeFilter !== 'All') params.append('test_type', testTypeFilter);

            const response = await axios.get(`${API_BASE_URL}/db/defects`, { params });
            setDefects(response.data);
        } catch (err) {
            setError('Failed to fetch defects. Please try again.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (defectId: string, newStatus: string) => {
        try {
            // Call the new backend endpoint
            //await axios.put(`${API_BASE_URL}/defect_management/defects/${defectId}/status`, {
            await axios.put(`${API_BASE_URL}/db/defects/${defectId}/status`, {
                status: newStatus,
            });

            // Update the local state to reflect the change immediately
            setDefects(prevDefects =>
                prevDefects.map(defect =>
                    defect.id === defectId ? { ...defect, status: newStatus as Defect['status'] } : defect
                )
            );
            if (onDefectUpdate) {
                onDefectUpdate();
            }
        } catch (err) {
            console.error('Failed to update defect status:', err);
            alert('Error: Could not update defect status.');
        }
    };


    // Fetch defects when the modal opens or filters change
    useEffect(() => {
        if (isOpen) {
            fetchDefects();
        }
    }, [isOpen, selectedAgent, severityFilter, statusFilter, testTypeFilter]);

    if (!isOpen) return null;

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'Critical': return '#721c24';
            case 'High': return '#dc3545';
            case 'Medium': return '#ffc107';
            case 'Low': return '#28a745';
            default: return '#6c757d';
        }
    };

    return (
        //<div className="modal-overlay" onClick={onClose}>
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1001 }}>
            <div className="modal-content fullscreen-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Defect Log for Agent: {selectedAgent}</h2>
                    <button onClick={onClose} className="close-button">✕</button>
                </div>

                {/* Filter Bar */}
                <div className="filter-bar" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <div>
                        <label>Severity: </label>
                        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
                            <option value="All">All</option>
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>
                    <div>
                        <label>Status: </label>
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                            <option value="All">All</option>
                            <option value="New">New</option>
                            <option value="Acknowledged">Acknowledged</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <div>
                        <label>Test Type: </label>
                        <select value={testTypeFilter} onChange={e => setTestTypeFilter(e.target.value)}>
                            <option value="All">All</option>
                            <option value="Functional">Functional</option>
                            <option value="Reliability">Reliability</option>
                            <option value="Guardrail">Guardrail</option>
                            <option value="Compliance">Compliance</option>
                        </select>
                    </div>
                    <button onClick={fetchDefects} disabled={isLoading}>Refresh</button>
                </div>

                <div className="modal-body" style={{ height: '70vh', overflow: 'auto' }}>
                    {isLoading ? <p>Loading defects...</p> : error ? <p style={{ color: 'red' }}>{error}</p> : (
                        <table className="results-table">
                            <thead>
                                <tr>
                                    <th>Defect ID</th>
                                    <th>Test Name</th>
                                    <th>Severity</th>
                                    <th>Status</th>
                                    <th>Test Type</th>
                                    <th>Failure Reason</th>
                                    <th>AI Analysis</th>
                                    <th>Created</th>
                                    <th>Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {defects.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>No defects found for this agent with the selected filters.</td>
                                    </tr>
                                ) : (
                                    defects.map(defect => (
                                        <tr key={defect.id}>
                                            <td>{defect.defect_id_str}</td>
                                            <td>{defect.test_name}</td>
                                            <td>
                                                <span style={{ color: 'white', backgroundColor: getSeverityColor(defect.severity), padding: '0.2rem 0.5rem', borderRadius: '12px', fontSize: '0.8rem' }}>
                                                    {defect.severity}
                                                </span>
                                            </td>
                                            <td>
                                                <select
                                                    value={defect.status}
                                                    onChange={(e) => handleStatusChange(defect.id, e.target.value)}
                                                    style={{ padding: '0.25rem', borderRadius: '4px' }}
                                                >
                                                    <option value="New">New</option>
                                                    <option value="Acknowledged">Acknowledged</option>
                                                    <option value="In Progress">In Progress</option>
                                                    <option value="Resolved">Resolved</option>
                                                    <option value="Closed">Closed</option>
                                                </select>
                                            </td>
                                            <td>{defect.test_type}</td>
                                            <td>{defect.failure_reason}</td>
                                            <td><CollapsibleCell content={defect.ai_analysis} /></td>
                                            <td>{new Date(defect.created_at).toLocaleString()}</td>
                                            <td>{new Date(defect.updated_at).toLocaleString()}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="modal-actions">
                    <button className="pushable push-secondary" onClick={onClose}>
                        <span className="shadow"></span>
                        <span className="edge"></span>
                        <span className="front">Close</span>
                    </button>
                </div>
            </div>
        </div>


    );
};

export default DefectsModal;